import {
  FormLayout,
  Modal,
  TextField,
  Form,
  Checkbox,
  Select,
  Text,
  SkeletonBodyText,
  Button,
  InlineStack,
  BlockStack,
  List,
  Divider,
} from '@shopify/polaris'; // 1. Import 'Select'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import helpers from 'helpers';

import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';

import __ from 'languages/index';
import { TypedJourney, useGetJourneys, useMutateCreateJourney, useMutateGetJourney, useUpdateJourney } from 'queries/journey.query';
import TextFieldRichText from 'components/TextFieldRichText';
import { useGetSeatings } from 'queries/seating.query';
import AutocompleteAddress from 'components/addressAutoComplete';
const provinceData = require('config/provinceData.json');

const journeyTypeOptions = [
  { label: 'Chiều đi', value: 'outbound' },
  { label: 'Chiều về', value: 'inbound' },
];

/**
 * Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

export default function JourneyModal({ onClose, show, currentJourneyID }: { onClose: Function; show: boolean; currentJourneyID?: bigint }) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateJourney();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateJourney();
  const { mutateAsync: getVehicle, data: vehicleData } = useMutateGetJourney();

  const [entity, setEntity] = useState<TypedJourney>(null);
  const journeyFromGeofenceRef = useRef<HTMLInputElement>(null);
  const journeyToGeofenceRef = useRef<HTMLInputElement>(null);
  const [uploadingFromGeofence, setUploadingFromGeofence] = useState(false);
  const [uploadingToGeofence, setUploadingToGeofence] = useState(false);

  const [seatingOptions, setSeatingOptions] = useState([]);
  const { data, isLoading: seatingLoading } = useGetSeatings({
    limit: 20,
    sort: 'createdAt:desc',
  });
  const { data: parentJourneyData, isLoading: parentJourneyLoading } = useGetJourneys({
    journey_parent: null,
    limit: 100,
    sort: 'createdAt:asc',
  });
  const parentJourneyOptions = useMemo(
    () => [
      { label: 'Không có hành trình cha', value: '' },
      ...(parentJourneyData?.body || [])
        .filter((journey) => String(journey.id) !== String(currentJourneyID || ''))
        .map((journey) => ({
          label: journey.journey_name || 'Hành trình chưa đặt tên',
          value: String(journey.id),
        })),
    ],
    [currentJourneyID, parentJourneyData]
  );

  useEffect(() => {
    if (!helpers.isEmpty(data)) {
      let { body } = data;
      let totalResult = [
        {
          label: 'Không chọn',
          value: '',
        },
      ];
      for (let seating of body) {
        totalResult.push({
          label: seating.seating_name,
          value: seating.id,
        });
      }

      setSeatingOptions(totalResult);
    }
  }, [data]);

  // 2. Chuyển đổi JSON thành options cho component Select
  const provinceOptions = [
    { label: 'Chọn một địa điểm', value: '' }, // Thêm một lựa chọn mặc định
    ...Object.entries(provinceData).map(([id, name]) => ({
      label: String(name),
      value: String(id),
    })),
  ];

  useEffect(() => {
    if (currentJourneyID && show) {
      getVehicle(currentJourneyID);
    } else {
      setEntity(null);
    }
  }, [currentJourneyID, show]);

  const useFields = {
    journey_name: useField<string>({
      value: entity?.journey_name ?? '',
      validates: [notEmpty('Không được để trống trường này.'), lengthLessThan(250, 'No more than 250 characters.')],
    }),
    journey_parent: useField<string>({
      value: entity?.journey_parent != null ? String(entity.journey_parent) : '',
      validates: [],
    }),
    journey_type: useField<string>({
      value: entity?.journey_type || 'outbound',
      validates: [],
    }),

    journey_description: useField<string>({
      value: entity?.journey_description ?? '',
      validates: [notEmpty('Không được để trống trường này.'), lengthLessThan(65000, 'No more than 65000 characters.')],
    }),

    journey_default_price: useField<number>({
      value: entity?.journey_default_price ?? 1,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
    journey_total_time: useField<string>({
      value: String(entity?.journey_total_time ?? 0),
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
    journey_distance: useField<string>({
      value: String(entity?.journey_distance ?? 0),
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    // journey_from vẫn giữ lại để lưu tên tỉnh thành (ví dụ: "Thành Phố Hà Nội")
    journey_from: useField<string>({
      value: entity?.journey_from ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),
    journey_from_geo_name: useField<string>({
      value: entity?.journey_from_geo_name ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),
    journey_from_lat_long: useField<string>({
      value: entity?.journey_from_lat_long ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),

    // journey_to vẫn giữ lại để lưu tên tỉnh thành (ví dụ: "Thành Phố Hải Dương")
    journey_to: useField<string>({
      value: entity?.journey_to ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),
    journey_to_geo_name: useField<string>({
      value: entity?.journey_to_geo_name ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),
    journey_to_lat_long: useField<string>({
      value: entity?.journey_to_lat_long ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),
    // 3. Cập nhật useField cho các trường _geo để lưu ID
    // Các validate không cần thiết nữa vì người dùng chọn từ danh sách có sẵn
    journey_from_province_id: useField<string>({
      value: entity?.journey_from_province_id ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),

    journey_to_province_id: useField<string>({
      value: entity?.journey_to_province_id ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),
    journey_from_geofence_id: useField<string>({
      value: entity?.journey_from_geofence_id ?? '',
      validates: [],
    }),
    journey_to_geofence_id: useField<string>({
      value: entity?.journey_to_geofence_id ?? '',
      validates: [],
    }),

    journey_from_geofence_id_media_filename: useField<string>({
      value: entity?.journey_journey_from_geofence_media?.media_filename ?? '',
      validates: [],
    }),
    journey_to_geofence_id_media_filename: useField<string>({
      value: entity?.journey_journey_to_geofence_media?.media_filename ?? '',
      validates: [],
    }),

    journey_default_seating: useField<string>({ value: entity?.journey_default_seating, validates: [] }),
    is_default: useField<number>({ value: entity?.is_default, validates: [] }),

    journey_status: useField<number>({
      value: entity?.journey_status ?? 1,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
  };

  const {
    fields,
    submit,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new
        if (!currentJourneyID) {
          await createEntity({
            journey_name: values.journey_name,
            journey_parent: values.journey_parent || null,
            journey_type: values.journey_type,
            journey_description: values.journey_description,
            journey_from: values.journey_from,
            journey_from_geo_name: values.journey_from_geo_name,
            journey_from_lat_long: values.journey_from_lat_long,
            journey_from_province_id: values.journey_from_province_id,
            journey_from_geofence_id: values.journey_from_geofence_id,
            journey_to: values.journey_to,
            journey_to_geo_name: values.journey_to_geo_name,
            journey_to_lat_long: values.journey_to_lat_long,
            journey_to_province_id: values.journey_to_province_id,
            journey_to_geofence_id: values.journey_to_geofence_id,
            journey_total_time: helpers.parseNumeric(values.journey_total_time),
            journey_distance: helpers.parseNumeric(values.journey_distance),
            journey_default_price: helpers.parseNumeric(values.journey_default_price),
            journey_default_seating: values.journey_default_seating || null,
            journey_status: values.journey_status,
            is_default: values.is_default,
          });
        } else {
          await updateEntity({
            id: String(currentJourneyID),
            journey_name: values.journey_name,
            journey_parent: values.journey_parent || null,
            journey_type: values.journey_type,
            journey_description: values.journey_description,
            journey_from: values.journey_from,
            journey_from_geo_name: values.journey_from_geo_name,
            journey_from_lat_long: values.journey_from_lat_long,
            journey_from_province_id: values.journey_from_province_id,
            journey_from_geofence_id: values.journey_from_geofence_id,
            journey_to: values.journey_to,
            journey_to_geo_name: values.journey_to_geo_name,
            journey_to_lat_long: values.journey_to_lat_long,
            journey_to_province_id: values.journey_to_province_id,
            journey_to_geofence_id: values.journey_to_geofence_id,
            journey_total_time: helpers.parseNumeric(values.journey_total_time),
            journey_distance: helpers.parseNumeric(values.journey_distance),
            journey_default_price: helpers.parseNumeric(values.journey_default_price),
            journey_default_seating: values.journey_default_seating,
            journey_status: values.journey_status,
            is_default: values.is_default,
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });
  /* End form */

  useEffect(() => {
    if (vehicleData) {
      setEntity(vehicleData); // Giữ lại để các phần khác của component có thể dùng

      // Dùng hàm resetForm để cập nhật toàn bộ giá trị cho form
      // Đây là bước quan trọng nhất để sửa lỗi
      resetForm();
    }
  }, [vehicleData, resetForm]); // Thêm resetForm vào dependency array

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      resetForm();
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  useEffect(() => {
    if (!currentJourneyID) {
      resetForm();
      setEntity(null);
    }
  }, [currentJourneyID]);

  const handleProvinceChange = (
    selectedId: string,
    fieldName: 'journey_from' | 'journey_to',
    fieldGeoName: 'journey_from_province_id' | 'journey_to_province_id'
  ) => {
    const selectedLabel = provinceData[selectedId] || '';
    fields[fieldGeoName].onChange(selectedId);
    fields[fieldName].onChange(selectedLabel);
  };

  const handleUploadGeofence = async (event: ChangeEvent<HTMLInputElement>, fieldName: 'journey_from_geofence_id' | 'journey_to_geofence_id') => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const setUploading = fieldName === 'journey_from_geofence_id' ? setUploadingFromGeofence : setUploadingToGeofence;
    const inputRef = fieldName === 'journey_from_geofence_id' ? journeyFromGeofenceRef : journeyToGeofenceRef;

    setUploading(true);
    try {
      const bodyFormData = new FormData();
      bodyFormData.append('files', selectedFile);
      const response = await axios.post('media/upload', bodyFormData);
      fields[fieldName].onChange(response?.data?.media_id ?? '');
      fields[fieldName + '_media_filename'].onChange(response?.data?.media_filename ?? '');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentJourneyID ? 'Cập nhật hành trình' : 'Tạo hành trình'}
      primaryAction={{
        content: currentJourneyID ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: updating || creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField autoFocus autoComplete="off" requiredIndicator label="Tên hành trình" {...fields.journey_name} />
            {parentJourneyLoading ? (
              <SkeletonBodyText lines={1} />
            ) : (
              <Select label="Hành trình cha" options={parentJourneyOptions} {...fields.journey_parent} />
            )}
            <Select label="Chiều hành trình" options={journeyTypeOptions} {...fields.journey_type} />
            <FormLayout.Group condensed>
              <TextField
                label="Số phút thực hiện (Phút)"
                type="number"
                autoComplete="off"
                {...fields.journey_total_time}
                helpText={fields.journey_total_time.value ? `Tương đương ${helpers.minuteToFullHourString(fields.journey_total_time.value)}` : ''}
              />
              <TextField label="Khoảng cách (KM)" type="number" autoComplete="off" {...fields.journey_distance} />
              <TextField
                label="Giá mặc định (VND)"
                autoComplete="off"
                {...fields.journey_default_price}
                value={helpers.formatNumberWithCommas(fields.journey_default_price.value)}
                onChange={(a) => fields.journey_default_price.onChange(helpers.parseNumeric(a))}
              />
            </FormLayout.Group>

            {/* 4. Thay thế TextField bằng Select */}
            {seatingLoading ? (
              <SkeletonBodyText />
            ) : (
              <Select
                label="Loại hình vận chuyển?"
                requiredIndicator
                options={seatingOptions}
                onChange={(selectedId) => {
                  fields.journey_default_seating.onChange(selectedId);
                }}
                value={fields.journey_default_seating.value} // Giá trị của Select là ID (lấy từ trường _geo)
                error={fields.journey_default_seating.error} // Hiển thị lỗi từ trường _geo
              />
            )}

            {/* 4. Thay thế TextField bằng Select */}
            <Select
              label="Từ đâu?"
              requiredIndicator
              options={provinceOptions}
              onChange={(selectedId) => handleProvinceChange(selectedId, 'journey_from', 'journey_from_province_id')}
              value={fields.journey_from_province_id.value} // Giá trị của Select là ID (lấy từ trường _geo)
              error={fields.journey_from_province_id.error} // Hiển thị lỗi từ trường _geo
            />

            <AutocompleteAddress
              onChange={(text) => {
                fields.journey_from_geo_name.onChange(text);
              }}
              selectedCallback={(prediction) => {
                let { latlng } = prediction;
                fields.journey_from_lat_long.onChange(latlng);
              }}
              value={fields.journey_from_geo_name.value}
              autoComplete={''}
              label="Điểm xuất phát để tính KM "
              requiredIndicator
              error={fields.journey_from_geo_name.error}
            />
            <Text as="p">
              Lat {fields.journey_from_lat_long.value?.split(',')[0]}, Lng {fields.journey_from_lat_long.value?.split(',')[1]}
            </Text>
            <InlineStack gap="200" align="start">
              <input
                ref={journeyFromGeofenceRef}
                type="file"
                accept=".geojson,application/geo+json,application/json"
                style={{ display: 'none' }}
                onChange={(event) => handleUploadGeofence(event, 'journey_from_geofence_id')}
              />
              <Button loading={uploadingFromGeofence} onClick={() => journeyFromGeofenceRef.current?.click()}>
                Upload geojson điểm đi
              </Button>
              {fields.journey_from_geofence_id.value ? (
                <Button
                  tone="critical"
                  onClick={() => {
                    fields.journey_from_geofence_id.onChange('');
                    fields.journey_from_geofence_id_media_filename.onChange('');
                  }}
                >
                  Xóa file
                </Button>
              ) : null}
            </InlineStack>
            <Text as="p">Media ID geofence điểm đi: {fields.journey_from_geofence_id_media_filename.value || 'Chưa có'}</Text>

            <Select
              label="Đến đâu?"
              requiredIndicator
              options={provinceOptions}
              onChange={(selectedId) => handleProvinceChange(selectedId, 'journey_to', 'journey_to_province_id')}
              value={fields.journey_to_province_id.value} // Giá trị của Select là ID (lấy từ trường _geo)
              error={fields.journey_to_province_id.error} // Hiển thị lỗi từ trường _geo
            />

            <AutocompleteAddress
              onChange={(text) => {
                fields.journey_to_geo_name.onChange(text);
              }}
              selectedCallback={(prediction) => {
                let { latlng } = prediction;
                fields.journey_to_lat_long.onChange(latlng);
              }}
              value={fields.journey_to_geo_name.value}
              autoComplete={''}
              requiredIndicator
              error={fields.journey_to_geo_name.error}
              label="Điểm đến để tính KM "
            />
            <Text as="p">
              Lat {fields.journey_to_lat_long.value?.split(',')[0]}, Lng {fields.journey_to_lat_long.value?.split(',')[1]}
            </Text>
            <InlineStack gap="200" align="start">
              <input
                ref={journeyToGeofenceRef}
                type="file"
                accept=".geojson,application/geo+json,application/json"
                style={{ display: 'none' }}
                onChange={(event) => handleUploadGeofence(event, 'journey_to_geofence_id')}
              />
              <Button loading={uploadingToGeofence} onClick={() => journeyToGeofenceRef.current?.click()}>
                Upload geojson điểm đến
              </Button>
              {fields.journey_to_geofence_id.value ? (
                <Button
                  tone="critical"
                  onClick={() => {
                    fields.journey_to_geofence_id.onChange('');
                    fields.journey_to_geofence_id_media_filename.onChange('');
                  }}
                >
                  Xóa file
                </Button>
              ) : null}
            </InlineStack>
            <Text as="p">Media ID geofence điểm đến: {fields.journey_to_geofence_id_media_filename.value || 'Chưa có'}</Text>

            <TextFieldRichText
              label={'Mô tả'}
              initialData={fields.journey_description.value}
              onCallback={(v) => fields.journey_description.onChange(v)}
              error={fields.journey_description.error}
            />

            <Checkbox
              label="Đánh dấu là hành trình mặc định?"
              checked={Boolean(fields.is_default.value === 1)}
              onChange={(e) => fields.is_default.onChange(Number(e))}
            />
            <Checkbox
              label="Hoạt động"
              checked={Boolean(fields.journey_status.value === 1)}
              onChange={(e) => fields.journey_status.onChange(Number(e))}
            />
          </FormLayout>

          <br />
          <Divider />
          <br />

          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">
              Lưu ý về file GEO JSON
            </Text>

            <Text as="p">
              Sử dụng tool{' '}
              <a href="https://geojson.io/next/" target="_blank" rel="nofollow noreferrer">
                GEOJSON Editor
              </a>{' '}
              để chỉnh sửa vùng.
            </Text>
            <List>
              <List.Item>Sử dụng key "pickup_support" (number 0|1) cho phép đón trả</List.Item>
              <List.Item>Sử dụng key "note" (string) cho ghi chú</List.Item>
              <List.Item>Sử dụng key "position_name" (string) Tên vùng</List.Item>
              <List.Item>Sử dụng key "price" (number) cho giá cộng thêm / giảm</List.Item>
              <List.Item>Sử dụng key "pickup_time" (number) cho số phút cộng thêm khi đón</List.Item>
              <List.Item>Sử dụng key "dropoff_time"(number) cho số phút cộng thêm khi trả</List.Item>
            </List>
          </BlockStack>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}
